import React, { Fragment } from 'react';
import moment from 'moment';
import QueryString from 'query-string';
import { Redirect, NavLink } from 'react-router-dom';
import styles from './AgentDetail.scss';
import classNames from 'classnames/bind';
import {
  Container,
  Header,
  Button,
  Badge,
  MetaInfo,
  Modal,
  Recommendation,
  Spinner,
  Stars,
  RatingBar,
} from '../common';

import history from '~/history.js';
import { isMobile } from '~/utils/BrowserUtils.js';

const cx = classNames.bind(styles);

const RATING_MAP = {
  expertise: '전문성',
  responsiveness: '응답성',
  reliability: '신뢰성',
  price: '가격만족도',
};

const favoredBadge = favored =>
  favored && (
    <div className={cx('agent-detail__favored-badge')}>
      <Badge border="primary" text="primary">
        인기
      </Badge>
    </div>
  );

const agentName = name => (
  <div className={cx('agent-detail__name')}>{name}</div>
);

const agentClients = clients => (
  <div className={cx('agent-detail__clients')}>
    <span className="u-text-important">{clients}</span>개 사업장 이용 중
  </div>
);

const agentStats = (views, calls, reviews, phoneNumber) => (
  <div className={cx('agent-detail__stats')}>
    <MetaInfo icon="fa fa-eye">{views}명 조회</MetaInfo>
    <MetaInfo icon="fa fa-phone">
      {phoneNumber === '' ? '연락처 없음' : `${calls}명 연락`}
    </MetaInfo>
    {reviews !== '' && (
      <MetaInfo icon="fa fa-comment">실고객사 리뷰 {reviews}</MetaInfo>
    )}
  </div>
);

const agentCallButton = (id, phoneNumber, actions) =>
  isMobile() &&
  phoneNumber !== '' && (
    <div className={cx('agent-detail__call')}>
      <Button
        block
        size="small"
        iconName="fa fa-phone"
        iconAlign="left"
        onClick={() => actions.callAgent(id)}
        href={`tel:${phoneNumber}`}
      >
        상담 전화
      </Button>
    </div>
  );

const favoredDescription = favored =>
  favored && (
    <div className={cx('agent-detail__favored-description')}>
      <i className="fa fa-heart" />
      많은 고객사가 이용하는 세무대리인입니다.
    </div>
  );

const tabs = (id, reviews, tab) => (
  <div className={cx('agent-detail__tabs')}>
    <NavLink
      className={cx('agent-detail__tab')}
      isActive={() => !tab || tab === 'info'}
      replace
      to={`/agents/${id}?tab=info`}
    >
      정보
    </NavLink>
    <NavLink
      className={cx('agent-detail__tab')}
      isActive={() => tab === 'reviews'}
      replace
      to={`/agents/${id}?tab=reviews`}
    >
      리뷰 {reviews}
    </NavLink>
  </div>
);

const phoneItem = phoneNumber =>
  !isMobile() &&
  phoneNumber !== '' && (
    <div className={cx('agent-detail__more-info-item')}>
      <div className={cx('agent-detail__info')}>연락처</div>
      <div className={cx('agent-detail__info-description')}>{phoneNumber}</div>
    </div>
  );

const addressItem = address => (
  <div className={cx('agent-detail__more-info-item')}>
    <div className={cx('agent-detail__info')}>세무대리인 위치</div>
    <div className={cx('agent-detail__info-description')}>{address}</div>
  </div>
);

const clientsItem = (clients, classifications) =>
  clients !== 0 && (
    <div className={cx('agent-detail__more-info-item')}>
      <div className={cx('agent-detail__info')}>
        이용 중인 캐시노트 고객사 업종
      </div>
      <div className={cx('agent-detail__info-description')}>
        {classifications.map((cl, i) => (
          <Badge key={i} multiple text="secondary">
            {cl}
          </Badge>
        ))}
      </div>
    </div>
  );

const infoCta = actions => (
  <div className={cx('agent-detail__cta')}>
    <div className={cx('agent-detail__cta-title')}>캐시노트 맞춤 추천</div>
    <div className={cx('agent-detail__recommendations')}>
      <Recommendation
        icon="fa fa-check-square-o"
        buttonText="맞춤 비교견적 받기"
        onButtonClick={() => {
          window.location = 'https://offline.cashnote.kr/tax_requests/new';
        }}
      >
        공식인증 세무대리인의 비교견적을 무료로 확인하세요!
      </Recommendation>
      <Recommendation
        icon="fa fa-bar-chart"
        buttonText="세무대행 맞춤 시세"
        onButtonClick={() => actions.showModal('cost')}
      >
        내 사업장에 적정한 세무대행 비용이 궁금하다면 지금 무료로 확인하세요!
      </Recommendation>
      <Recommendation
        icon="fa fa-lightbulb-o"
        buttonText="절세 체크리스트"
        onButtonClick={() => actions.showModal('cut')}
      >
        현재 세금 절감 상태를 진단하고 맞춤형 절세 방안을 무료로 추천받으세요!
      </Recommendation>
    </div>
  </div>
);

const reviewsNum = reviews => (
  <div className={cx('agent-detail__reviews-num')}>
    실 고객사 리뷰 <span className="u-color-primary">{reviews}</span>
  </div>
);

const reviewsRatings = ratings => (
  <div className={cx('agent-detail__reviews-ratings')}>
    <div className={cx('agent-detail__reviews-ratings-overall')}>
      <Stars size="large" current={ratings.overall} />
    </div>
    <div className={cx('agent-detail__reviews-ratings-others')}>
      {Object.entries(RATING_MAP).map(rating => (
        <RatingBar
          key={rating[0]}
          current={ratings[rating[0]]}
          label={rating[1]}
        />
      ))}
    </div>
  </div>
);

const reviewsList = reviewList => (
  <div className={cx('agent-detail__reviews-list')}>
    {reviewList.map(review => (
      <div key={review.id} className={cx('agent-detail__reviews-list-item')}>
        <div className={cx('agent-detail__reviews-list-item-header')}>
          <div className={cx('agent-detail__reviews-list-item-header-icon')}>
            <i className="fa fa-user-o" />
          </div>
          <div>
            <div className={cx('agent-detail__reviews-list-item-kic')}>
              {review.classification}
            </div>
            <div className={cx('agent-detail__reviews-list-item-dates')}>
              <Stars current={review.overall} hideNumber size="small" />
              {moment(review.createdAt).format('LL')}
            </div>
          </div>
        </div>
        <div className={cx('agent-detail__reviews-list-item-content')}>
          {review.text}
        </div>
      </div>
    ))}
  </div>
);

const moreReviewsButton = getNextReviews => (
  <div className={cx('agent-detail__more-reviews')}>
    <Button size="small" background="white" onClick={getNextReviews}>
      더 보기
    </Button>
  </div>
);

const reviewsNeedLogin = agentId => {
  const query = QueryString.stringify({
    redirect: `/agents/${agentId}?tab=reviews`,
  });

  return (
    <div className={cx('agent-detail__reviews-need-login')}>
      <div className={cx('agent-detail__reviews-need-login-text')}>
        실고객사의 리뷰를 보시려면
        <br />
        캐시노트에 로그인해주세요!
      </div>
      <Button
        square
        block
        background="secondary"
        size="small"
        href={`/login?${query}`}
      >
        캐시노트 로그인
      </Button>
    </div>
  );
};

const reviewsEmpty = () => (
  <div className={cx('agent-detail__reviews-empty')}>
    등록된 리뷰가 없습니다.
  </div>
);

const reviewCta = () => (
  <div className={cx('agent-detail__cta')}>
    <div className={cx('agent-detail__cta-title')}>리뷰 등록 이벤트</div>
    <div className={cx('agent-detail__recommendations')}>
      <Recommendation
        icon="fa fa-pencil-square-o"
        buttonText="내 세무대리인 리뷰 쓰기"
        onButtonClick={() => history.push('/reviews/new')}
      >
        내 세무대리인의 리뷰를 작성하고{' '}
        <span className="u-color-primary">캐시노트 고급형 무료이용권</span>을
        받으세요!
      </Recommendation>
    </div>
  </div>
);

const signupContent = (text, actions) => (
  <div className={cx('agent-detail__modal-signup')}>
    <div className={cx('agent-detail__modal-signup-description')}>
      캐시노트에 가입하신 다음 홈택스를 연동하시면 카카오톡에서 {text} 무료로
      확인할 수 있습니다.
    </div>
    <div className={cx('agent-detail__modal-button')}>
      <Button
        block
        round={false}
        background="secondary"
        size="small"
        href="https://app.cashnote.kr/signup"
        onClick={() => actions.closeModal()}
      >
        무료 가입
      </Button>
    </div>
  </div>
);

const chatContent = (text, actions) => (
  <div className={cx('agent-detail__modal-chat')}>
    <div className={cx('agent-detail__modal-chat-title')}>
      이미 캐시노트에 가입하셨다면!
    </div>
    <div className={cx('agent-detail__modal-chat-description')}>
      카카오톡의 캐시노트 채팅화면에서
      <br />'{text}'를 눌러 확인하세요.
    </div>
    <div className={cx('agent-detail__modal-button')}>
      <Button
        block
        round={false}
        background="white"
        size="small"
        href="http://pf.kakao.com/_Gxhqjd/chat"
        onClick={() => actions.closeModal()}
      >
        채팅화면 이동
      </Button>
    </div>
  </div>
);

const modals = (modalIdShown, actions) => (
  <div>
    {modalIdShown === 'cost' && (
      <Modal
        className={cx('agent-detail__modal')}
        title="세무대행 맞춤 시세"
        onHide={() => actions.closeModal()}
      >
        {signupContent('내 사업장에 적정한 세무비용을', actions)}
        {chatContent('세무대행 맞춤 시세', actions)}
      </Modal>
    )}
    {modalIdShown === 'cut' && (
      <Modal
        className={cx('agent-detail__modal')}
        title="절세 체크리스트"
        onHide={() => actions.closeModal()}
      >
        {signupContent('내 사업장의 절세 진단 결과를', actions)}
        {chatContent('절세 체크리스트', actions)}
      </Modal>
    )}
  </div>
);

const AgentDetail = ({
  AgentDetailActions,
  agentDetail,
  isLoggedIn,
  getNextReviews,
  loading,
  error,
  location,
}) => {
  const {
    id,
    name,
    address,
    phoneNumber,
    clients,
    views,
    calls,
    reviews,
    classifications,
    ratings,
    favored,
    modalIdShown,
    reviewList,
    isMoreReviews,
  } = agentDetail;

  // redirect to root if failed to get the agent from id
  if (error) {
    return <Redirect to="/" />;
  }

  const basicInfo = (
    <div className={cx('agent-detail__basic-info')}>
      {favoredBadge(favored)}
      {agentName(name)}
      <div className={cx('agent-detail__metas')}>
        <div className={cx('agent-detail__meta-row')}>
          {agentClients(clients)}
          <Stars
            className={cx('agent-detail__overall-rating')}
            current={ratings.overall}
            size="small"
          />
        </div>
        <div className={cx('agent-detail__meta-row')}>
          {agentStats(views, calls, reviews, phoneNumber)}
        </div>
      </div>
      {agentCallButton(id, phoneNumber, AgentDetailActions)}
      {favoredDescription(favored)}
    </div>
  );

  const moreInfoContent = (
    <div className={cx('agent-detail__more-info')}>
      {phoneItem(phoneNumber)}
      {addressItem(address)}
      {clientsItem(clients, classifications)}
    </div>
  );

  const reviewsContent = (
    <div className={cx('agent-detail__reviews')}>
      {reviewsNum(reviews)}
      {reviews === '0' && reviewsEmpty()}
      {reviews !== '0' && isLoggedIn && (
        <Fragment>
          {reviewsRatings(ratings)}
          {reviewsList(reviewList)}
          {isMoreReviews && moreReviewsButton(getNextReviews)}
        </Fragment>
      )}
      {reviews !== '0' && !isLoggedIn && reviewsNeedLogin(id)}
    </div>
  );

  const query = QueryString.parse(location.search);
  const content =
    !loading && id ? (
      <Fragment>
        {basicInfo}
        {tabs(id, reviews, query.tab)}
        {(!query || !query.tab || query.tab === 'info') && (
          <div id="tab-info" className={cx('agent-detail__tab-content')}>
            {moreInfoContent}
            {infoCta(AgentDetailActions)}
          </div>
        )}
        {query.tab === 'reviews' && (
          <div id="tab-reviews" className={cx('agent-detail__tab-content')}>
            {reviewsContent}
            {reviewCta(isLoggedIn)}
          </div>
        )}
      </Fragment>
    ) : (
      <Spinner />
    );

  return (
    <Container className={cx('agent-detail')}>
      <Header title="세무대리인 정보" />
      {content}
      {modals(modalIdShown, AgentDetailActions)}
    </Container>
  );
};

export default AgentDetail;



// WEBPACK FOOTER //
// ./src/components/AgentDetail/AgentDetail.js